/* Hero steps and address lookup for the existing single-page advice form.
 * The existing Forms bridge owns sending,
 * consent, attribution, deduplication and receipt-confirmed conversions. */
(() => {
  'use strict';
  const SELECTOR = '[data-zv-advice-intake]';
  const API = 'https://api.pdok.nl/bzk/locatieserver/search/v3_1/';
  const initialise = (root = document) => {
    const forms = [...(root.matches?.(SELECTOR) ? [root] : []), ...root.querySelectorAll(SELECTOR)];
    forms.forEach(bind);
  };
  function bind(form) {
    if (form.dataset.zvIntakeReady === 'true') return;
    const $ = (selector) => form.querySelector(selector);
    const single = form.hasAttribute('data-zv-intake-single');
    const copy = JSON.parse($('[data-zv-intake-copy]').textContent);
    const query = $('[data-zv-address-query]');
    const options = $('[data-zv-address-options]');
    const addressSearch = $('[data-zv-address-search]');
    const status = $('[data-zv-address-status]');
    const nextButton = $('[data-zv-intake-next]') || (single ? $('button[type="submit"]') : null);
    const setStatus = (message, visible = false) => {
      status.textContent = message;
      status.parentElement.classList.toggle('is-visible', visible);
    };
    const manualPanel = $('[data-zv-address-manual]');
    const postcode = $('[data-zv-field="Postcode"]');
    const number = $('[data-zv-field="Huisnummer"]');
    const addition = $('[data-zv-field="Huisnummertoevoeging"]');
    const phone = $('[data-zv-field="Phone"]');
    const name = $('[data-zv-field="First name"]');
    const email = $('[data-zv-field="Email"]');
    let step = 1, manual = false, selected = null, results = [], active = -1;
    let timer, controller, generation = 0;
    let lastQueryValue = query.value;
    const cleanPostcode = (value) => value.replace(/\s/g, '').toUpperCase();
    const isBusy = () => form.dataset.zvFormBusy === 'true';
    const closeOptions = () => {
      options.hidden = true;
      query.setAttribute('aria-expanded', 'false');
      query.removeAttribute('aria-activedescendant');
      active = -1;
    };
    const cancelSearch = () => {
      clearTimeout(timer);
      generation += 1;
      controller?.abort();
      if (nextButton) nextButton.disabled = false;
      query.removeAttribute('aria-busy');
      closeOptions();
    };
    const clearError = (index) => {
      const box = $(`[data-zv-step-error="${index}"]`) || $('[data-zv-form-error]');
      box.hidden = true;
      box.textContent = '';
    };
    const error = (index, message, field) => {
      const box = $(`[data-zv-step-error="${index}"]`) || $('[data-zv-form-error]');
      box.textContent = message;
      box.hidden = false;
      if (field) {
        field.setAttribute('aria-invalid', 'true');
        field.focus({ preventScroll: true });
        field.scrollIntoView({ block: 'nearest', behavior: 'auto' });
      }
      return false;
    };
    const setManual = (value) => {
      manual = value;
      cancelSearch();
      $('[data-zv-address-search]').hidden = manual;
      manualPanel.hidden = !manual;
      query.required = !manual;
      setStatus('');
      clearError(1);
      if (!manual) {
        selected = null;
        query.value = '';
        lastQueryValue = '';
        postcode.value = number.value = addition.value = '';
      }
    };
    const showStep = (value, focus = true) => {
      if (single) return;
      step = value;
      form.dataset.zvIntakeStepActive = String(step);
      closeOptions();
      form.querySelectorAll('[data-zv-intake-step]').forEach((part) => {
        const inactive = part.dataset.zvIntakeStep !== String(step);
        part.hidden = inactive;
        part.inert = inactive;
        part.setAttribute('aria-hidden', String(inactive));
      });
      if (focus) {
        const target = step === 2 ? name : manual ? postcode : query;
        target.focus({ preventScroll: true });
        // A step change must not move the page or the photo. On a phone,
        // only compensate when the focused field is genuinely clipped by
        // the sticky header or the visible (keyboard-reduced) viewport.
        if (window.matchMedia('(max-width: 749px)').matches) {
          window.requestAnimationFrame(() => {
            const rect = target.getBoundingClientRect();
            const viewport = window.visualViewport;
            const viewportTop = viewport?.offsetTop || 0;
            const viewportBottom = viewportTop + (viewport?.height || window.innerHeight);
            const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 68;
            const visibleTop = viewportTop + header + 12;
            const visibleBottom = viewportBottom - 12;
            const delta = rect.top < visibleTop ? rect.top - visibleTop
              : rect.bottom > visibleBottom ? rect.bottom - visibleBottom : 0;
            if (delta) window.scrollBy({ top: delta, behavior: 'auto' });
          });
        }
      }
    };
    const validateFirst = () => {
      clearError(1);
      if (!manual && (!selected || query.value !== selected.label)) {
        showStep(1, false);
        return error(1, copy.chooseAddress, query);
      }
      postcode.value = cleanPostcode(postcode.value);
      number.value = number.value.trim();
      addition.value = addition.value.trim();
      if (!/^[1-9][0-9]{3}[A-Z]{2}$/.test(postcode.value) || !/^[1-9][0-9]{0,4}$/.test(number.value)) {
        showStep(1, false);
        setManual(true);
        return error(1, copy.invalidAddress, !postcode.checkValidity() ? postcode : number);
      }
      return true;
    };
    const validateLast = () => {
      clearError(2);
      name.value = name.value.trim();
      email.value = email.value.trim();
      if (!name.value) return error(2, copy.invalidName, name);
      const digits = phone.value.replace(/\D/g, '');
      if (!/^[+\d\s().-]+$/.test(phone.value) || digits.length < 10 || digits.length > 15) {
        return error(2, copy.invalidPhone, phone);
      }

      if (!email.value || !email.checkValidity()) return error(2, copy.invalidEmail, email);
      return true;
    };
    async function request(path, params, signal) {
      const response = await fetch(`${API}${path}?${new URLSearchParams(params)}`, {
        signal, credentials: 'omit', referrerPolicy: 'no-referrer'
      });
      if (!response.ok) throw new Error('address-service-unavailable');
      return response.json();
    }
    const setActive = (index) => {
      active = index;
      [...options.children].forEach((item, i) => item.setAttribute('aria-selected', String(i === active)));
      if (options.children[active]) {
        query.setAttribute('aria-activedescendant', options.children[active].id);
        options.children[active].scrollIntoView({ block: 'nearest' });
      }
    };
    async function choose(index) {
      if (isBusy() || !results[index]) return;
      const id = results[index].id;
      cancelSearch();
      const epoch = generation;
      if (nextButton) nextButton.disabled = true;
      query.setAttribute('aria-busy', 'true');
      const requestController = new AbortController();
      controller = requestController;
      const timeout = setTimeout(() => requestController.abort(), 6000);
      setStatus(copy.searching);
      try {
        const data = await request('lookup', { id, fl: 'weergavenaam,postcode,huisnummer,huisletter,huisnummertoevoeging' }, requestController.signal);
        if (epoch !== generation || manual || !form.isConnected) return;
        const item = data.response?.docs?.[0];
        if (!item?.postcode || !item?.huisnummer || !item?.weergavenaam) throw new Error('incomplete-address');
        selected = { label: item.weergavenaam };
        query.value = selected.label;
        lastQueryValue = query.value;
        postcode.value = cleanPostcode(item.postcode);
        number.value = String(item.huisnummer);
        addition.value = [item.huisletter, item.huisnummertoevoeging].filter(Boolean).join(' ');
        query.removeAttribute('aria-invalid');
        clearError(1);
        setStatus('');
      } catch (_) {
        if (epoch === generation && !manual && form.isConnected) {
          // Only a real lookup failure reveals the canonical fallback fields.
          // There is no competing manual-entry action in the normal flow.
          setManual(true);
          setStatus(copy.unavailable, true);
          postcode.focus({ preventScroll: true });
        }
      } finally {
        clearTimeout(timeout);
        if (epoch === generation) {
          if (nextButton) nextButton.disabled = false;
          query.removeAttribute('aria-busy');
        }
      }
    }
    async function suggest(value, epoch) {
      const requestController = new AbortController();
      controller = requestController;
      const timeout = setTimeout(() => requestController.abort(), 6000);
      setStatus(copy.searching);
      try {
        const data = await request('suggest', { q: value, fq: 'type:adres', rows: '5' }, requestController.signal);
        if (epoch !== generation || manual || !form.isConnected || document.activeElement !== query) return;
        results = data.response?.docs || [];
        options.replaceChildren();
        results.forEach((item, index) => {
          const option = document.createElement('li');
          option.id = `${form.id}-option-${index}`;
          option.setAttribute('role', 'option');
          option.setAttribute('aria-selected', 'false');
          option.textContent = item.weergavenaam;
          // Keep input focus for mouse selection; touch/click also works.
          option.addEventListener('mousedown', (event) => event.preventDefault());
          option.addEventListener('click', () => choose(index));
          options.append(option);
        });
        options.hidden = results.length === 0;
        query.setAttribute('aria-expanded', String(results.length > 0));
        setStatus(results.length ? copy.chooseResult : copy.noResults, !results.length);
      } catch (_) {
        if (epoch === generation && !manual && form.isConnected) {
          // Only a real lookup failure reveals the canonical fallback fields.
          // There is no competing manual-entry action in the normal flow.
          setManual(true);
          setStatus(copy.unavailable, true);
          postcode.focus({ preventScroll: true });
        }
      } finally { clearTimeout(timeout); }
    }
    const queryChanged = () => {
      // Safari contact autofill can emit change without an input event.
      // Do not cancel an in-flight search again for the same value.
      if (query.value === lastQueryValue) return;
      lastQueryValue = query.value;
      cancelSearch();
      selected = null;
      postcode.value = number.value = addition.value = '';
      query.removeAttribute('aria-invalid');
      clearError(1);
      const value = query.value.trim();
      setStatus('');
      if (value.length < 3) return;
      const epoch = generation;
      timer = setTimeout(() => suggest(value, epoch), 350);
    };
    query.addEventListener('input', queryChanged);
    query.addEventListener('change', queryChanged);
    query.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') { cancelSearch(); setStatus(''); }
      if (options.hidden || !results.length) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        setActive(event.key === 'ArrowDown' ? (active + 1) % results.length : (active - 1 + results.length) % results.length);
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        // Do not silently choose an address the visitor has not highlighted.
        if (active >= 0) choose(active);
      }
      if (event.key === 'Tab') closeOptions();
    });
    // Touching a suggestion may blur the input before click is dispatched.
    // A blur timeout can hide that target while the visitor is still tapping.
    // Dismiss only when interacting outside the complete address picker.
    document.addEventListener('pointerdown', (event) => {
      if (!addressSearch.contains(event.target)) closeOptions();
    });
    document.addEventListener('focusin', (event) => {
      if (!addressSearch.contains(event.target)) closeOptions();
    });
    $('[data-zv-intake-next]')?.addEventListener('click', () => {
      if (!isBusy() && validateFirst()) showStep(2);
    });
    $('[data-zv-intake-back]')?.addEventListener('click', () => { if (!isBusy()) showStep(1); });
    form.addEventListener('input', (event) => {
      event.target.removeAttribute('aria-invalid');
      const part = event.target.closest('[data-zv-intake-step]');
      if (part) clearError(Number(part.dataset.zvIntakeStep));
      else if (single) clearError(1);
    });
    // Capture precedes the bridge's bubbling submit handler. Enter in step 1
    // only advances; invalid fields cannot reach Shopify or conversion code.
    form.addEventListener('submit', (event) => {
      const stop = () => {
        event.preventDefault();
        event.stopImmediatePropagation();
      };
      if (isBusy()) { stop(); return; }
      if (!single && step === 1) {
        stop();
        if (validateFirst()) showStep(2);
        return;
      }
      if (!validateFirst() || !validateLast()) stop();
      else if (single && !form.checkValidity()) { stop(); form.reportValidity(); }
    }, true);
    form.dataset.zvIntakeStepActive = '1';
    form.dataset.zvIntakeReady = 'true';
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialise());
  else initialise();
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
